import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { includes, map } from 'es-toolkit/compat';
import { unwoundRowMetadataFromId } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { slidesBaseFields } from 'interfaces/procedure/fields/slideFields';
import React from 'react';

import {
  DataGridInterfaceWrapper,
  DataGridInterfaceWrapperProps,
} from '../ProceduresPage/DataGrids/DataGridInterfaceWrapper';

const manifestFieldKeys = [
  'label',
  'slideId',
  'originalFileName',
  'stainingType',
  'cancerTypeId',
  'cancerSubtypes',
  'biopsySiteId',
  'biopsyType',
  'preservationMethod',
  'procedureType',
  'metastasis',
  'positiveControl',
  'negativeControl',
];
const manifestFieldLabelOverrides = { label: 'New case name', slideId: 'Slide ID' };

interface CaseManifestPlanTableProps {
  labId: string;
  editableCaseIds: number[];
  editableSlideIds: string[];
  editingPaused: boolean;
  gridProps: DataGridInterfaceWrapperProps;
  hasDraftCases: boolean;
  isCaseLabelEditable: (slideId: string) => boolean;
  onShowOnlyErrorsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onShowOnlyUnassignedSlidesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUseFileNameForNewCaseLabelsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUnassignedSlidesLoading: boolean;
  hasUnassignedSlidesError: boolean;
  showOnlyErrors: boolean;
  showOnlyUnassignedSlides: boolean;
  useFileNameForNewCaseLabels: boolean;
}

export const CaseManifestPlanTable = ({
  labId,
  editableCaseIds,
  editableSlideIds,
  editingPaused,
  gridProps,
  isCaseLabelEditable,
  onShowOnlyErrorsChange,
  onShowOnlyUnassignedSlidesChange,
  onUseFileNameForNewCaseLabelsChange,
  isUnassignedSlidesLoading,
  hasUnassignedSlidesError,
  showOnlyErrors,
  showOnlyUnassignedSlides,
  useFileNameForNewCaseLabels,
}: CaseManifestPlanTableProps) => (
  <Box my={1} sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
    <Grid container alignItems="center" p={0.5} sx={{ columnGap: 3, rowGap: 1 }}>
      <Grid item>
        <Grid container alignItems="center">
          <Grid item>
            <Typography>Show only issues</Typography>
          </Grid>
          <Grid item>
            <Switch
              checked={showOnlyErrors}
              size="small"
              onChange={onShowOnlyErrorsChange}
              inputProps={{ 'aria-label': 'Show only issues' }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container alignItems="center">
          <Grid item>
            <Typography>Show only slides awaiting assignment</Typography>
          </Grid>
          <Grid item>
            <Switch
              checked={showOnlyUnassignedSlides}
              size="small"
              onChange={onShowOnlyUnassignedSlidesChange}
              inputProps={{ 'aria-label': 'Show only slides awaiting assignment' }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <FormControlLabel
          control={
            <Switch checked={useFileNameForNewCaseLabels} size="small" onChange={onUseFileNameForNewCaseLabelsChange} />
          }
          label="Use file names for new case labels"
          sx={{ m: 0 }}
        />
      </Grid>
    </Grid>
    {showOnlyUnassignedSlides && hasUnassignedSlidesError && (
      <Typography role="status" sx={{ mb: 1 }}>
        Could not load slides awaiting assignment.
      </Typography>
    )}
    {showOnlyUnassignedSlides &&
      !isUnassignedSlidesLoading &&
      !hasUnassignedSlidesError &&
      gridProps.totalSlides === 0 && (
        <Typography role="status" sx={{ mb: 1 }}>
          {showOnlyErrors ? 'No slides awaiting assignment need attention.' : 'No slides are awaiting assignment.'}
        </Typography>
      )}
    {!showOnlyUnassignedSlides && showOnlyErrors && gridProps.totalSlides === 0 && (
      <Typography role="status" sx={{ mb: 1 }}>
        No rows need attention.
      </Typography>
    )}
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
      <DataGridInterfaceWrapper
        {...gridProps}
        additionalSlideColumns={[
          ...(gridProps.additionalSlideColumns || []),
          {
            field: 'sourceSlideUri',
            headerName: 'Source path',
            width: 300,
            sortable: false,
            editable: false,
            renderCell: ({ row }) => (
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                {row.labId === labId && row.sourceSlideUri ? row.sourceSlideUri : 'Unavailable'}
              </Typography>
            ),
          },
        ]}
        columnOrder={[
          'currentCaseAssignment',
          'targetCaseAssignment',
          'label',
          'assignmentChange',
          'slideId',
          'originalFileName',
          'sourceSlideUri',
        ]}
        includedFieldKeys={manifestFieldKeys}
        fieldLabelOverrides={manifestFieldLabelOverrides}
        disableEditing={false}
        pendingSlidesMode={false}
        forceSlidesMode
        hideActionsMenu
        dataGridProps={{
          isCellEditable: (params) => {
            if (editingPaused) return false;
            const { baseRowId, innerRowId: slideId } = unwoundRowMetadataFromId(params.row.id);
            const isSlideField = includes(map(slidesBaseFields, 'dataKey'), params.field);
            const isEditableManifestRow = includes(editableSlideIds, slideId);
            const isEditableManifestCaseField = includes(editableCaseIds, Number(baseRowId));
            if (!isEditableManifestRow || params.field === 'originalFileName') return false;
            if (params.field === 'label' && !isCaseLabelEditable(String(slideId))) return false;
            return isSlideField || isEditableManifestCaseField;
          },
        }}
        hideLinksToViewer
        hideCaseIdColumn
      />
    </Box>
  </Box>
);
